import { useMutation, useQueryClient } from "@tanstack/react-query";
import { vaultKeys } from "../queryKeys";
import {
  bdcApi,
  type BdcStartRequest,
  type BdcStateResponse,
} from "../../../api/bdc.api";
import { useAppSelector } from "../../../redux/hooks";
import { getVaultActorAuthScope } from "../../../utils/vault/vaultActorSession";

interface BdcStartParams {
  reqId: string;
  body?: BdcStartRequest;
}

/**
 * POST bdc/start — DISPARA el NIP (idempotente).
 *
 * IMPORTANTE: la llamada debe originarse en una acción explícita del usuario
 * (click "Enviar NIP"), NUNCA en un `useEffect` de montaje.
 *
 * NO invalida el folder (el requirement aún no es Valid); en su lugar refresca
 * la cache de `bdc/status` con el estado devuelto para mantener el resume en
 * sync. NO swallowea el error: el componente mapea el code con `mapBdcAxiosError`
 * y dirige los datos rechazados a su paso de corrección existente.
 */
export function useBdcStart(folderId?: string) {
  const queryClient = useQueryClient();
  const jwtToken = useAppSelector((state) => state.vault.jwtToken);
  const authScope = getVaultActorAuthScope(jwtToken);

  return useMutation<BdcStateResponse, unknown, BdcStartParams>({
    mutationFn: ({ reqId, body }) => bdcApi.start(reqId, body),
    onMutate: async ({ reqId }) => {
      await queryClient.cancelQueries({
        queryKey: vaultKeys.bdcStatus(reqId, authScope),
      });
    },
    onSuccess: async (data, { reqId }) => {
      const queryKey = vaultKeys.bdcStatus(reqId, authScope);
      // Reconnection can start another GET while the POST is in flight.
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(
        queryKey,
        (prev: BdcStateResponse | undefined) => ({
          ...prev,
          ...data,
          // The mutation may change the local verdict. Keep source metadata,
          // but do not carry the preceding GET's obsolete requirement status.
          requirement_status: data.requirement_status,
        }),
      );
      // Si por idempotencia el reporte ya estaba completado, el requirement
      // pasó a Valid -> refrescar el folder.
      if (
        ["completed", "in_review", "invalid"].includes(data.status) &&
        folderId
      ) {
        void queryClient.invalidateQueries({
          queryKey: vaultKeys.folder(folderId),
        });
      }
    },
  });
}
